import { useContext, useEffect, useState  } from "react";
import { StockControlContext } from "./StockControlProvider";

import useNotify from "../notify";
import InventoryService from "../../services/inventoryService";
import StockControlService from "../../services/stockControlService";

import { utils } from "../../utils/utils";
import { stockControllUtils } from "../../utils/stockControll-utils";

const useStockControl = (warehouseId, stockControlId, branchCode, createdBy) => {
  const [stockControl, reducer] = useContext(StockControlContext);
  const [loading, setLoading] = useState(false);
  const { Notify, variants } = useNotify();

  useEffect(() => {
    const getStockControl = async () => {
      setLoading(true);
      StockControlService.Get(stockControlId)
        .then((sc) =>
          reducer({
            type: "REPLACE",
            stockControl: {
              ...sc,
              details: sc.details.map((detail) => {
                const difference = stockControllUtils.calculateDiffernce(detail.quantity, detail.expectedQuantity);
                const differenceToShow = stockControllUtils.formatDiferrence( detail.difference );

                return {
                  ...detail,
                  name: detail.name || "Producto No Encontrado",
                  difference,
                  differenceToShow,
                }
              }),
            },
          })
        )
        .finally(() => setLoading(false));
    };

    if (
      !!stockControlId &&
      (!stockControl || stockControl.id !== Number(stockControlId))
    )
      getStockControl();
  }, [stockControlId, stockControl, reducer]);

  const searchProduct = async (refId) => {
    setLoading(true);
    const product = await InventoryService.Search({ warehouseIds: [warehouseId], refId })
      .catch(() => console.error(`Product ${refId} not found.`))
      .finally(() => setLoading(false));

    if (!product?.productId)
      return;

    return {
      productId: product.productId,
      refId: product.refId,
      colorCode: product.colorCode,
      sizeCode: product.sizeCode || 'U',
      name: product.description,
      expectedQuantity: product.quantity,
    };
  };

  const searchProducts = async (refIds) => {
    setLoading(true);

    const skusByWarehouse = refIds?.map(sku => ({ sku, warehouseId }));

    const products = await InventoryService.Search({ skusByWarehouse })
      .catch(() => console.error(`Product information could not be accessed`))
      .finally(() => setLoading(false));

    if (!products?.values)
      return;

    return products?.values?.map((product) => ({
      productId: product.productId,
      refId: product.refId,
      colorCode: product.colorCode,
      sizeCode: product.sizeCode || 'U',
      name: product.description,
      expectedQuantity: product.quantity,
    }));
  };

  const searchProductsByCategories = async (categories) => {
    setLoading(true);
    const products = await InventoryService.Search({ warehouseIds: [warehouseId], isExport: true, categoryIds: categories})
      .catch(() => console.error(`Product information could not be accessed`))
      .finally(() => setLoading(false));

      if (!products?.values)
        return;

      return products?.values?.map((product) => ({
        id: product.productId,
        color: product.colorCode,
        refId: product.refId,
        name: product.description,
        expectedQuantity: product.quantity,
      }));
  }

  const addProduct = async (refId) => {
    const parsedRefId = stockControllUtils.parseRefId(refId);
    
    if (parsedRefId.error) 
      return Notify(parsedRefId.error, variants.ERROR);

    const details = stockControl.details.map((d) => d);
    const existing = details.find((existing) =>
      compareRefId(existing.refId, parsedRefId.sku)
    );

    if (!existing) {
      const product = await searchProduct(parsedRefId.sku);
      if (!product) 
        return Notify(`No se encontró el producto ${parsedRefId.sku}`, variants.ERROR);
      add( details, product, parsedRefId.sku, parsedRefId.quantity );
    } else update(existing, parsedRefId.quantity);

    edit({ details });
    return true;
  };

  const addProductMassive = async ( refIds, overwrite = false, quantity = 1) => {
    const details = stockControl.details.map((d) => ({ ...d, overwritten: !overwrite }));
    const parseRefIds = refIds.map(refId => stockControllUtils.parseRefId(refId, quantity));

    const newRefIds = [...new Set(parseRefIds
      .filter(item => !item.error)
      .filter(item => !details.some((existing) => compareRefId(existing.refId, item.sku)))
    )];

    const existingRefIds = parseRefIds
      .filter(item => !item.error)
      .filter(item => details.some((existing) => compareRefId(existing.refId, item.sku)));

    const notFoundRefIds = [];

    if( newRefIds.length > 0 ) {
      const products = await searchProducts(newRefIds.map(r => r.sku));
  
      newRefIds.forEach(parsedRefId => {
        const product = products.find((p) => p.refId === parsedRefId.sku);
        if (product)
          add( details, product, parsedRefId.sku, parsedRefId.quantity );
        else notFoundRefIds.push(parsedRefId.sku);
      });
    }

    existingRefIds
      .forEach(parsedRefId => {
        const detail = details.find((detail) => compareRefId(detail.refId, parsedRefId.sku));

        if (!detail.overwritten) {
          detail.overwritten = true;
          detail.quantity = 0;
        }

        update(detail, parsedRefId.quantity)
      });

    const refIdsWithError = parseRefIds.filter(item => !!item.error);

    if( refIdsWithError.length > 0 )
      utils.downloadOnTxt( refIdsWithError, "skus-con-errores.txt" )

    edit({ details });

    if (notFoundRefIds.length > 0) {
      const link = document.createElement('a');
      link.download = 'NotFound-skus.txt';
      const blob = new Blob([notFoundRefIds.join('\n')], { type: 'text/plain' });
      link.href = window.URL.createObjectURL(blob);
      link.click();
    }
    
    return true;
  };

  const removeProduct = (refId) => {
    if (stockControl.details.some((d) => d.refId === refId)) {
      edit({ details: stockControl.details.filter((d) => d.refId !== refId) });
      return true;
    }
  };

  const changeProduct = async (refId, payload) => {
    const details = stockControl.details.filter((d) => d.refId !== refId);
    const existing = details.find((existing) =>
      compareRefId(existing.refId, payload.refId)
    );

    if (!existing) {
      const product = await searchProduct(payload.refId);
      add( details, product, payload.refId, payload.quantity, payload.observation );
    } else 
      update(existing, payload.quantity, payload.observation);

    return edit({ details });
  };

  const reCheck = () => {
    edit({ details: stockControl.details.filter((d) => d.difference !== 0) });
  };

  const createAdjustment = (onSuccess) => {
    setLoading(true);
    StockControlService.CreateAdjustment(stockControl, warehouseId)
      .then((stockControl) => {
        reducer({ type: "REPLACE", stockControl });
        Notify("Ajuste creado con éxito", variants.SUCCESS);
        onSuccess();
      })
      .catch(() => {
        console.error(`Error al crear el ajuste`);
        Notify("Ocurrió un error al crear el ajuste", variants.ERROR);
      })
      .finally(() => setLoading(false));
  };

  const productNotFound = (refId) => {
    return {
      productId: 0,
      refId,
      colorCode: "U",
      sizeCode: "U",
      name: "Producto no encontrado",
      expectedQuantity: 0,
    }
  }

  const add = ( details, product, refId, quantity ) => {
    const addedProduct = product ?? productNotFound(refId);
    const expectedQuantity = addedProduct?.expectedQuantity || 0;
    const difference = stockControllUtils.calculateDiffernce(quantity, expectedQuantity)
    const differenceToShow = stockControllUtils.formatDiferrence(difference);
        
    details.push({
      ...addedProduct,
      quantity,
      expectedQuantity,
      difference,
      differenceToShow,
    });
  };

  const update = (detail, quantity, observation = '') => {
    detail.quantity += quantity;
    detail.difference = stockControllUtils.calculateDiffernce(detail.quantity, detail.expectedQuantity);
    detail.differenceToShow = stockControllUtils.formatDiferrence( detail.difference );
    detail.observation = observation;
  };

  const compareRefId = (existingRefId, refId) =>
    existingRefId.toLowerCase() === refId.toLowerCase();

  const editProduct = (refId, payload) => {
    if (stockControl.details.some((d) => d.refId === refId)) {
      const details = stockControl.details.map((detail) => {
        payload.difference = stockControllUtils.calculateDiffernce(payload.quantity, payload.expectedQuantity);
        payload.differenceToShow = stockControllUtils.formatDiferrence( payload.difference );
        return detail.refId === refId ? { ...detail, ...payload } : detail;
      });

      edit({ details });

      return true;
    }
  };

  const edit = (payload) => 
    reducer({ type: "EDIT", payload });

  const save = async () => {
    const Save = stockControl.id
      ? StockControlService.Update
      : StockControlService.Create

    setLoading(true);
    return Save(stockControl, warehouseId, branchCode, createdBy)
      .then((stockControl) => reducer({ type: "REPLACE", stockControl }))
      .finally(() => setLoading(false));
  };

  return {
    stockControl,
    loading,
    edit,
    save,
    reCheck,
    addProduct,
    addProductMassive,
    removeProduct,
    changeProduct,
    editProduct,
    createAdjustment,
    searchProductsByCategories
  };
};

export default useStockControl;
